if (!customElements.get('scroll-spy')) {
  customElements.define(
    'scroll-spy',
    class ScrollSpy extends HTMLElement {
      constructor() {
        super();

        this.activeClass = this.dataset.activeClass || 'is-active';
        this.smoothScroll = this.dataset.smoothScroll === 'true';
        this.rootMargin = this.dataset.rootMargin || '-20% 0px -70% 0px';
        this.scrollOffset = Number.parseFloat(this.dataset.scrollOffset) || 0;
        this.links = [];
        this.selects = [];
        this.targets = new Map();
        this.visibleTargets = new Set();
        this.observer = null;
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.onLinkClick = this.onLinkClick.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.onIntersect = this.onIntersect.bind(this);
      }

      connectedCallback() {
        this.links = Array.from(this.querySelectorAll('a[href*="#"]')).filter((link) => {
          const target = this.getTargetFromLink(link);

          if (!target) {
            return false;
          }

          this.registerTarget(target, 'links', link);
          link.addEventListener('click', this.onLinkClick);

          return true;
        });

        this.selects = Array.from(this.querySelectorAll('select')).filter((select) => {
          let hasTargetOption = false;

          Array.from(select.options).forEach((option) => {
            const target = this.getTargetFromValue(option.value);

            if (!target) {
              return;
            }

            hasTargetOption = true;
            this.registerTarget(target, 'selects', select);
          });

          if (hasTargetOption) {
            select.addEventListener('change', this.onSelectChange);
          }

          return hasTargetOption;
        });

        if (!this.targets.size || !('IntersectionObserver' in window)) {
          return;
        }

        this.observer = new IntersectionObserver(this.onIntersect, {
          rootMargin: this.rootMargin,
          threshold: 0
        });

        this.targets.forEach(({ target }) => {
          this.observer.observe(target);
        });
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        this.links.forEach((link) => {
          link.removeEventListener('click', this.onLinkClick);
          link.classList.remove(this.activeClass);
        });

        this.selects.forEach((select) => {
          select.removeEventListener('change', this.onSelectChange);
        });

        this.links = [];
        this.selects = [];
        this.targets.clear();
        this.visibleTargets.clear();
      }

      registerTarget(target, itemKey, item) {
        if (!this.targets.has(target.id)) {
          this.targets.set(target.id, { links: [], selects: [], target });
        }

        const targetItems = this.targets.get(target.id)[itemKey];

        if (!targetItems.includes(item)) {
          targetItems.push(item);
        }
      }

      getTargetFromLink(link) {
        const href = link.getAttribute('href');

        if (!href || !href.includes('#')) {
          return null;
        }

        let url;

        try {
          url = new URL(href, window.location.href);
        } catch {
          return null;
        }

        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
          return null;
        }

        return this.getTargetFromHash(url.hash);
      }

      getTargetFromValue(value) {
        if (!value || !value.includes('#')) {
          return null;
        }

        let url;

        try {
          url = new URL(value, window.location.href);
        } catch {
          return this.getTargetFromHash(value);
        }

        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
          return null;
        }

        return this.getTargetFromHash(url.hash);
      }

      getTargetFromHash(hash) {
        if (!hash || hash === '#') {
          return null;
        }

        try {
          return document.getElementById(decodeURIComponent(hash.slice(1)));
        } catch {
          try {
            return document.querySelector(hash);
          } catch {
            return null;
          }
        }
      }

      onLinkClick(event) {
        if (!this.smoothScroll || this.prefersReducedMotion.matches) {
          return;
        }

        const target = this.getTargetFromLink(event.currentTarget);

        if (!target) {
          return;
        }

        event.preventDefault();
        this.scrollToTarget(target);
        this.updateHistory(target.id);
        this.setActiveTarget(target.id);
      }

      onSelectChange(event) {
        const target = this.getTargetFromValue(event.currentTarget.value);

        if (!target) {
          return;
        }

        this.scrollToTarget(target);
        this.updateHistory(target.id);
        this.setActiveTarget(target.id);
      }

      scrollToTarget(target) {
        if (!this.smoothScroll || this.prefersReducedMotion.matches) {
          window.scrollTo({
            top: this.getTargetScrollPosition(target),
            behavior: 'auto'
          });
          return;
        }

        window.scrollTo({
          top: this.getTargetScrollPosition(target),
          behavior: 'smooth'
        });
      }

      updateHistory(targetId) {
        if (window.history && window.history.pushState) {
          window.history.pushState(null, '', `#${targetId}`);
        } else {
          window.location.hash = targetId;
        }
      }

      getTargetScrollPosition(target) {
        const targetTop = target.getBoundingClientRect().top + window.scrollY;
        const scrollMarginTop = Number.parseFloat(window.getComputedStyle(target).scrollMarginTop);
        const targetOffset = Number.isNaN(scrollMarginTop) ? this.scrollOffset : scrollMarginTop;

        return Math.max(targetTop - targetOffset, 0);
      }

      onIntersect(entries) {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.visibleTargets.add(entry.target.id);
          } else {
            this.visibleTargets.delete(entry.target.id);
          }
        });

        const activeTargetId = this.getActiveTargetId();

        if (!activeTargetId) {
          return;
        }

        const activeItem = this.targets.get(activeTargetId);

        if (activeItem) {
          this.setActiveTarget(activeTargetId);
        }
      }

      getActiveTargetId() {
        const visibleIds = Array.from(this.visibleTargets);

        if (!visibleIds.length) {
          return '';
        }

        return visibleIds
          .map((id) => this.targets.get(id))
          .filter(Boolean)
          .sort(
            (a, b) => a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top
          )[0].target.id;
      }

      setActiveLink(activeLink) {
        this.setActiveLinks([activeLink]);
      }

      setActiveTarget(targetId) {
        const activeItem = this.targets.get(targetId);

        if (!activeItem) {
          return;
        }

        this.setActiveLinks(activeItem.links);
        this.selects.forEach((select) => {
          const matchingOption = Array.from(select.options).find((option) => {
            const target = this.getTargetFromValue(option.value);

            return target && target.id === targetId;
          });

          if (matchingOption) {
            select.value = matchingOption.value;
          }
        });
      }

      setActiveLinks(activeLinks) {
        this.links.forEach((link) => {
          link.classList.toggle(this.activeClass, activeLinks.includes(link));
        });
      }
    }
  );
}
